import { defineStore } from 'pinia'

interface PopupPlacement {
  left: number
  top: number
  width: number
  arrowOffset: number
}

interface ListenerCleanup {
  (): void
}

export const INPUT_ATTRIBUTE = 'data-qonizer-autocomplete-input'
export const INPUT_CLASS = 'qonizer-bridge-input'
export const WRAPPER_ATTRIBUTE = 'data-qonizer-autocomplete-wrapper'
export const WRAPPER_ACTIVE_ATTRIBUTE = 'data-qonizer-autocomplete-wrapper-active'
export const ROOT_ATTRIBUTE = 'data-qonizer-autocomplete-root'
export const BODY_PLACEMENT_ATTRIBUTE = 'data-qonizer-autocomplete-body-placement'

export const useBridgeStore = defineStore('BridgeStore', {
  state: () => ({
    clientInput: null as HTMLInputElement | null,
    clientInputWrapper: null as HTMLElement | null,
    clientInputCleanups: [] as ListenerCleanup[],
    searchButton: null as HTMLElement | null,
    searchButtonCleanup: null as ListenerCleanup | null,
    triggerElement: null as HTMLElement | null,
    triggerCleanup: null as ListenerCleanup | null,
    focusActivatorElement: null as HTMLElement | null,
    focusActivatorCleanup: null as ListenerCleanup | null,
    internalInputFocusHandler: null as (() => void) | null,
    popupPlacement: null as PopupPlacement | null,
    forceBodyPlacementHiddenInput: false,
    forceBodyPlacementBreakpoint: false
  }),
  getters: {
    hasClientInput (state): boolean {
      return !!state.clientInput
    },
    forceBodyPlacement (state): boolean {
      return state.forceBodyPlacementHiddenInput || state.forceBodyPlacementBreakpoint
    }
  },
  actions: {
    setClientInput (
      input: HTMLInputElement,
      wrapper: HTMLElement | null,
      cleanups: ListenerCleanup[]
    ): void {
      this.teardownClientInput()
      this.clientInput = input
      this.clientInputWrapper = wrapper
      this.clientInputCleanups = cleanups
    },
    teardownClientInput (): void {
      this.clientInputCleanups.forEach(cleanup => cleanup())
      this.clientInputCleanups = []

      if (this.clientInputWrapper) {
        this.clientInputWrapper.removeAttribute(WRAPPER_ACTIVE_ATTRIBUTE)
      }

      if (this.clientInput) {
        this.clientInput.removeAttribute(INPUT_ATTRIBUTE)
        this.clientInput.classList.remove(INPUT_CLASS)
      }

      this.clientInput = null
      this.clientInputWrapper = null
      this.popupPlacement = null
      this.forceBodyPlacementHiddenInput = false
      this.forceBodyPlacementBreakpoint = false

      if (typeof document !== 'undefined') {
        document.body.removeAttribute(BODY_PLACEMENT_ATTRIBUTE)
        document.documentElement?.removeAttribute(BODY_PLACEMENT_ATTRIBUTE)
        const root = document.getElementById('smartsearch-autocomplete')
        root?.removeAttribute(BODY_PLACEMENT_ATTRIBUTE)
      }
    },
    setBreakpointBodyPlacement (active: boolean): void {
      if (this.forceBodyPlacementBreakpoint === active) {
        return
      }
      this.forceBodyPlacementBreakpoint = active
    },
    setHiddenInputBodyPlacement (active: boolean): void {
      if (this.forceBodyPlacementHiddenInput === active) {
        return
      }
      this.forceBodyPlacementHiddenInput = active
    },
    activateWrapper (): void {
      if (this.clientInputWrapper) {
        this.clientInputWrapper.setAttribute(WRAPPER_ACTIVE_ATTRIBUTE, 'true')
      }
    },
    deactivateWrapper (): void {
      if (this.clientInputWrapper) {
        this.clientInputWrapper.removeAttribute(WRAPPER_ACTIVE_ATTRIBUTE)
      }
    },
    setSearchButton (button: HTMLElement, cleanup: ListenerCleanup): void {
      this.teardownSearchButton()
      this.searchButton = button
      this.searchButtonCleanup = cleanup
    },
    teardownSearchButton (): void {
      if (this.searchButtonCleanup) {
        this.searchButtonCleanup()
      }
      this.searchButtonCleanup = null
      this.searchButton = null
    },
    setTrigger (element: HTMLElement, cleanup: ListenerCleanup): void {
      this.teardownTrigger()
      this.triggerElement = element
      this.triggerCleanup = cleanup
    },
    teardownTrigger (): void {
      if (this.triggerCleanup) {
        this.triggerCleanup()
      }
      this.triggerCleanup = null
      this.triggerElement = null
    },
    setFocusActivator (element: HTMLElement, cleanup: ListenerCleanup): void {
      this.teardownFocusActivator()
      this.focusActivatorElement = element
      this.focusActivatorCleanup = cleanup
    },
    teardownFocusActivator (): void {
      if (this.focusActivatorCleanup) {
        this.focusActivatorCleanup()
      }
      this.focusActivatorCleanup = null
      this.focusActivatorElement = null
    },
    focusClientInput (): void {
      if (this.clientInput) {
        window.requestAnimationFrame(() => {
          this.clientInput?.focus()
        })
      }
    },
    registerInternalInputFocusHandler (handler: () => void): void {
      this.internalInputFocusHandler = handler
    },
    clearInternalInputFocusHandler (): void {
      this.internalInputFocusHandler = null
    },
    focusInternalInput (): void {
      if (this.internalInputFocusHandler) {
        window.requestAnimationFrame(() => {
          this.internalInputFocusHandler && this.internalInputFocusHandler()
        })
      }
    },
    updatePopupPlacement (): void {
      if (!this.clientInput) {
        this.popupPlacement = null
        this.setHiddenInputBodyPlacement(false)
        return
      }

      const rect = this.clientInput.getBoundingClientRect()
      const isHidden = rect.width < 1 || rect.height < 1

      if (isHidden) {
        this.popupPlacement = null
        this.setHiddenInputBodyPlacement(true)
        return
      }

      this.setHiddenInputBodyPlacement(false)

      if (this.forceBodyPlacement) {
        this.popupPlacement = null
        return
      }

      const horizontalMargin = 16
      const verticalOffset = 12
      const MAX_WIDTH = 960

      const viewportWidth = window.innerWidth
      const widthWithoutMargins = Math.max(0, viewportWidth - (horizontalMargin * 2))
      const width = Math.min(MAX_WIDTH, Math.max(rect.width, widthWithoutMargins))

      const idealLeftViewport = rect.left + (rect.width / 2) - (width / 2)
      const maxLeftViewport = Math.max(horizontalMargin, viewportWidth - horizontalMargin - width)
      const leftViewport = Math.min(Math.max(idealLeftViewport, horizontalMargin), maxLeftViewport)

      const left = leftViewport
      const top = rect.bottom + verticalOffset

      const inputCenter = rect.left + (rect.width / 2)
      const arrowOffset = Math.min(Math.max(inputCenter - left, 32), width - 32)

      this.popupPlacement = {
        left,
        top,
        width,
        arrowOffset
      }
    }
  }
})
